import { useState } from "react";
import { MessageSquarePlus, X, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import HoneypotField from "@/components/HoneypotField";
import { useSpamProtection } from "@/hooks/useSpamProtection";

const FloatingFeedbackButton = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [feedbackType, setFeedbackType] = useState("general");

  const {
    honeypotValue,
    setHoneypotValue,
    checkHoneypot,
    checkClientRateLimit,
    validateEmail,
    sanitizeText,
    calculateSpamScore,
    resetFormLoadTime,
    formLoadTime,
  } = useSpamProtection();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    // Client-side checks
    if (checkHoneypot()) {
      // Silently pretend success
      setSubmitted(true);
      return;
    }

    if (checkClientRateLimit(3, 600_000)) {
      toast.error("Too many submissions. Please wait a few minutes and try again.");
      return;
    }

    if (email) {
      const emailError = validateEmail(email);
      if (emailError) {
        toast.error(emailError);
        return;
      }
    }

    const spamCheck = calculateSpamScore({
      message: message.trim(),
      email: email.trim() || undefined,
      honeypotFilled: false,
    });

    setSubmitting(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("validate-submission", {
        body: {
          form_type: "feedback",
          honeypot: honeypotValue,
          time_on_page: (Date.now() - formLoadTime.current) / 1000,
          client_spam_score: spamCheck.score,
          email: email.trim() || null,
          feedback_type: feedbackType,
          message: sanitizeText(message.trim(), 1000),
          page_source: window.location.pathname,
          user_id: user?.id || null,
        },
      });

      if (fnError) throw fnError;
      if (data?.error) {
        toast.error(data.error);
        return;
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Feedback error:", err);
      toast.error("Could not submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setSubmitted(false);
      setEmail("");
      setMessage("");
      setFeedbackType("general");
      setHoneypotValue("");
      resetFormLoadTime();
    }, 300);
  };

  const handleOpen = () => {
    setOpen(true);
    resetFormLoadTime();
  };

  return (
    <>
      {!open && (
        <button
          onClick={handleOpen}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all hover:scale-105 text-sm font-medium"
          aria-label="Share Feedback"
        >
          <MessageSquarePlus className="h-4 w-4" />
          Share Feedback
        </button>
      )}

      {open && (
        <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-80 max-w-80 rounded-2xl border border-border/40 bg-card shadow-2xl animate-in slide-in-from-bottom-4 fade-in duration-200">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/30">
            <h3 className="font-display font-semibold text-sm">Share Feedback</h3>
            <button onClick={handleClose} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>

          {submitted ? (
            <div className="p-6 text-center">
              <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-3" />
              <p className="font-semibold mb-1">Thank you!</p>
              <p className="text-sm text-muted-foreground mb-4">Your feedback helps us grow a better tool for native gardeners.</p>
              <Button variant="outline" size="sm" onClick={handleClose}>Close</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4 space-y-3 relative">
              <HoneypotField value={honeypotValue} onChange={setHoneypotValue} />

              <div className="flex gap-1.5">
                {[
                  { value: "general", label: "💬 General" },
                  { value: "suggestion", label: "💡 Idea" },
                  { value: "bug", label: "🐛 Bug" },
                ].map((t) => (
                  <button
                    type="button"
                    key={t.value}
                    onClick={() => setFeedbackType(t.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      feedbackType === t.value
                        ? "bg-primary/10 border-primary/30 text-primary"
                        : "bg-muted/30 border-border/30 text-muted-foreground hover:bg-muted/50"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="What's on your mind?"
                  rows={3}
                  required
                  maxLength={1000}
                  className="text-sm resize-none"
                />
              </div>

              {!user && (
                <div>
                  <Label htmlFor="fb-email" className="text-xs">Email (optional)</Label>
                  <Input
                    id="fb-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    maxLength={255}
                    className="text-sm h-9"
                  />
                </div>
              )}

              <Button type="submit" variant="hero" size="sm" className="w-full gap-2" disabled={submitting || !message.trim()}>
                {submitting ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending...</>
                ) : (
                  <><Send className="h-3.5 w-3.5" /> Submit Feedback</>
                )}
              </Button>
            </form>
          )}
        </div>
      )}
    </>
  );
};

export default FloatingFeedbackButton;
